/**
 * API Client utility for making authenticated requests
 * Uses the authService to automatically handle token management
 */

import authService from '../services/authService';
import config from '../../config';

class ApiClient {
  constructor(baseURL = config.API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Make an authenticated GET request
   */
  async get(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await authService.makeAuthenticatedRequest(url, {
      method: 'GET',
      ...options
    });
    return this.handleResponse(response);
  }

  /**
   * Make an authenticated POST request
   */
  async post(endpoint, data = {}, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await authService.makeAuthenticatedRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
    return this.handleResponse(response);
  }

  /**
   * Make an authenticated PUT request
   */
  async put(endpoint, data = {}, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await authService.makeAuthenticatedRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
    return this.handleResponse(response);
  }

  /**
   * Make an authenticated DELETE request
   */
  async delete(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await authService.makeAuthenticatedRequest(url, {
      method: 'DELETE',
      ...options
    });
    return this.handleResponse(response);
  }

  /**
   * Handle API response
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Special handling for "Incident communication index not found" - don't throw, return structured error
      if (response.status === 404 && errorData.message === "Incident communication index not found") {
        return {
          success: false,
          message: errorData.message,
          status: 404
        };
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
